/* eslint-disable max-lines */
// deps
import React, { ReactElement, ReactNode, useContext, memo, useCallback, useMemo } from "react";
import classNames from "classnames";

// internal deps
import {
    BannerMessage,
    OfferBanner,
    OtherPayments,
    SplitPayBannerList,
    PaymentBanner,
    BANNER_TYPE,
    PaymentGroupDataDef,
    OffersStrip,
    PaymentMethodDowntimeHalfCard,
    SplitPayData,
    InAppUpiAwarenessHalfCard,
    BannerData,
    SplitPayRewardsHalfCard,
} from "@swiggy-private/payment-ui";
import { useEffectOnce, format, CartTimer, GenericCrouton, safeJsonParse } from "@swiggy-private/core-ui";
import {
    CheckoutContext,
    SwgyHeaderKeysList,
    AnalyticsContext,
    CheckoutActionTypes,
    getBridgeSdk,
    GlobalHeaderState,
    ApiContext,
} from "@swiggy-private/checkout-core";
import { isNonEmptyString } from "@swiggy-private/payment-core";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { ApiResponseJuspayMeta } from "@swiggy-private/payment-types";
import { BANNER_ACTION_TYPE, PaymentGroupBannerData } from "@payment-client/interfaces/Banner";

// components
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";
import { PaymentActionProvider } from "@payment-client/components/show-payments/components/payment-action-provider";
import { PaymentGroupHandler } from "@payment-client/components/show-payments/components/payment-group-handler";
import { OrderInterruptionAlert } from "@payment-client/components/order-interruption-alert";
import {
    AddMoneyConfirm,
    AddPaylaterOrderConfirm,
    AddSwiggyMoneyConfirm,
} from "@payment-client/components/add-money-confirm";
import { InvalidCouponAlert } from "@payment-client/components/invalid-coupon-alert";
import { FrictionGroupRenderer } from "@payment-client/components/show-payments/components/friction-group-renderer";
import PaymentFailed from "@payment-client/components/payment-failed";
import { GenericPaymentAlert } from "@payment-client/components/generic-payment-alert";
import { PaymentLoader } from "@payment-client/components/payment-loader";
import { PaymentAlreadyDoneAlert } from "@payment-client/components/payment-already-done-alert";
import { PaylaterLinkingFailed } from "@payment-client/components/show-payments/components/paylater-linking-failed";
import { GenericPaymentToast } from "@payment-client/components/generic-payment-toast";
import { WebviewRefreshOrderIdMismatchAlert } from "@payment-client/components/webview-refresh-orderid-mismatch-alert";

// contexts
import { CroutonContext } from "@payment-client/contexts/CroutonContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// hooks
import useSplitPay from "@payment-client/hooks/UseSplitPay";
import useFrictionLayout from "@payment-client/hooks/UseFrictionLayout";
import { useSwiggyMoneyActions } from "@payment-client/hooks/UseSwiggyMoneyActions";
import useCartTimer from "@payment-client/hooks/useCartTimer";
import { usePaymentBanner } from "@payment-client/hooks/UsePaymentBanner";
import useFlipkartCoins from "@payment-client/hooks/useFlipkartCoins";

// reducers
import { CheckoutMetaInfoResponsePayload, WebNavigateRequestPayload } from "@swiggy-private/native-bridge";

// analytics
import {
    onPaymentListImpressionEvent,
    paymentBannerImpressionEvent,
    paymentBannerClickEvent,
    unboxingBannerImpressionEvent,
    payUsingOtherMethodsImpressionEvent,
    payUsingOtherMethodsClickEvent,
} from "@payment-client/analytics/events/Payment";
import { offerStripImpressionEvent } from "@payment-client/analytics/events/OfferStrip";

// constants
import { SWIGGY_BANNER_CALLBACK_URL } from "@payment-client/constants/TextDict";
import { SWIGGY_PAY_PAYMENT_CODE } from "@payment-client/constants/SwiggyMoney";

// helpers
import {
    findSwiggyInstrumentFromSplitPayList,
    findSwiggyInstrumentsPaymentMethod,
} from "@payment-client/helpers/SwiggyInstrumentsHelper";
import { getCloudinaryIdFromURL, getPaymentGroupHeadingWithFilter } from "@payment-client/helpers/PaymentHelper";
import {
    canShowPaymentBanner,
    getBannerDimensions,
    getBannerParams,
    isInAppUPIBanner,
} from "@payment-client/helpers/PaymentBannerHelper";
import { getPayLaterPaymentGroup } from "@payment-client/helpers/PaylaterHelper";
import { isEmpty } from "@swiggy-private/js-utils";
import { getSelectedSplitpayMethodData } from "@payment-client/helpers/SplitPay";

// styles
import styles from "./PaymentUIHandler.v4.scss";
import { updateFrequencyCounterPublic } from "@payment-client/apis/publicPayment";

import { PaymentLoaderContext } from "@payment-client/contexts/PaymentLoaderContext";

interface PaymentUIHandlerProps extends Pick<PaymentGroupHandlerProps, "options"> {
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    couponApplicablePaymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    filteredPaymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    paymentBannerMessage?: string;
    showCouponGroupsOnly: boolean;
    showFilteredGroupsOnly: boolean;
    itemQuantity: number;
    showAllMethods: () => void;
    showSplitPay: boolean;
    amountToPay: number;
    amountToPayWithoutCouponWithoutSplitpay: number;
    couponCode?: string;
    paymentCouponCode?: string;
    rewardName?: string;
    splitpayList?: SplitPayData[];
    selectedSplitPayMethods?: string;
    banner?: GetPaymentMethodsResponse["banner"];
    withSplitPay: boolean;
    canShowAllMethods: boolean;
    checkoutMetaInfo?: CheckoutMetaInfoResponsePayload | null;
    juspayMeta: ApiResponseJuspayMeta;
    couponApplied: boolean;
    offerStrip?: GetPaymentMethodsResponse["offerBanner"];
    goToOffersPage?: () => void;
}

export function PaymentUIHandlerWrapper(props: {
    children: ReactNode;
    showFilteredGroupsOnly: boolean;
    showAllMethods: () => void;
    canShowAllMethods: boolean;
    couponCode?: string;
    paymentCouponCode?: string;
    rewardName?: string;
    offerStrip?: GetPaymentMethodsResponse["offerBanner"];
    goToOffersPage?: () => void;
    isIrctCart?: boolean;
    offerHeaderText?: GetPaymentMethodsResponse["offerHeaderText"];
}): ReactElement {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const { canShowAllMethods, children, couponCode, rewardName, paymentCouponCode } = props;

    const _couponCode = useMemo(() => {
        if (!isEmpty(paymentCouponCode)) {
            return paymentCouponCode;
        }
        return couponCode;
    }, [couponCode, paymentCouponCode]);

    const showOtherPayments = useMemo(() => {
        if (!canShowAllMethods) {
            return false;
        }
        return !isEmpty(_couponCode) || !isEmpty(rewardName);
    }, [canShowAllMethods, _couponCode, rewardName]);

    const offerStripImpression = useCallback(
        (offerText: string) => {
            offerStripImpressionEvent(offerText, impressionEvent);
        },
        [impressionEvent],
    );

    const payUsingOtherMethodsImpression = useCallback(() => {
        payUsingOtherMethodsImpressionEvent(impressionEvent, _couponCode);
    }, [impressionEvent, _couponCode]);

    const payUsingOtherMethodsClick = useCallback(() => {
        payUsingOtherMethodsClickEvent(clickEvent, _couponCode);
        props.showAllMethods();
    }, [clickEvent, _couponCode, props]);

    const displayOfferHeaderText = useMemo(() => {
        return props.offerHeaderText || "";
    }, [props.offerHeaderText]);

    const displayOfferCode = useMemo(() => {
        if (!isEmpty(props.couponCode) && !isEmpty(props.paymentCouponCode)) {
            return `${props.couponCode} + ${props.paymentCouponCode}`;
        }
        return props.couponCode || props.paymentCouponCode || "";
    }, [props.couponCode, props.paymentCouponCode]);

    useEffectOnce(() => {
        const cartId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartId) ?? "";
        const planId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.planid) ?? "";
        onPaymentListImpressionEvent(cartId, planId, impressionEvent, props?.isIrctCart);
    });

    if (!props.showFilteredGroupsOnly) {
        return (
            <div
                className={classNames(styles.container, {
                    [styles.containerUx4]: true,
                })}
            >
                {props.offerStrip && (
                    <OffersStrip
                        maxDiscount={props.offerStrip.maxDiscount}
                        icons={props.offerStrip.icons}
                        goToOffersPage={props.goToOffersPage}
                        impressionEvent={offerStripImpression}
                    />
                )}
                {props.children}
            </div>
        );
    }

    return (
        <div
            className={classNames(styles.container, {
                [styles.containerUx4]: true,
            })}
        >
            {!props.offerStrip && (displayOfferCode || displayOfferHeaderText) && (
                <OfferBanner offerCode={displayOfferCode} offerHeaderText={displayOfferHeaderText} />
            )}
            {props.offerStrip && (
                <OffersStrip
                    maxDiscount={props.offerStrip.maxDiscount}
                    icons={props.offerStrip.icons}
                    goToOffersPage={props.goToOffersPage}
                    impressionEvent={offerStripImpression}
                />
            )}
            {children}
            {showOtherPayments && (
                <OtherPayments
                    couponCode={_couponCode}
                    rewardName={rewardName}
                    onClick={payUsingOtherMethodsClick}
                    onLoad={payUsingOtherMethodsImpression}
                />
            )}
        </div>
    );
}

export const PaymentUIHandler = memo(function PaymentUIHandler({
    paymentGroups,
    filteredPaymentGroups,
    couponApplicablePaymentGroups,
    paymentBannerMessage,
    showAllMethods,
    showCouponGroupsOnly,
    showFilteredGroupsOnly,
    itemQuantity,
    canShowAllMethods,
    splitpayList,
    selectedSplitPayMethods,
    amountToPay,
    amountToPayWithoutCouponWithoutSplitpay,
    showSplitPay,
    withSplitPay,
    couponCode,
    paymentCouponCode,
    rewardName,
    banner,
    couponApplied,
    offerStrip,
    goToOffersPage,
    checkoutMetaInfo,
    ...props
}: PaymentUIHandlerProps): ReactElement | null {
    const { checkoutState } = useContext(CheckoutContext);
    const { croutonState } = useContext(CroutonContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { isLoading } = useContext(PaymentLoaderContext);
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();

    const {
        onSplitPayToggle,
        isRewardsBottomSheetVisible,
        setIsRewardsBottomSheetVisible,
        analyticsEvents: splitpayAnalyticsEvents,
    } = useSplitPay();
    const _paymentGroups = showFilteredGroupsOnly ? filteredPaymentGroups : paymentGroups;
    const onFlipkartToolTipShow = useFlipkartCoins(true);

    // When coupon or reward applied and is applicable to only 1 payment group, change the heading of payment group
    if (showFilteredGroupsOnly && _paymentGroups.length === 1) {
        _paymentGroups[0].displayName = getPaymentGroupHeadingWithFilter(
            _paymentGroups,
            paymentResponse?.rewardApplied,
        );
    }

    const frictionLayoutState = useFrictionLayout(_paymentGroups, canShowAllMethods);
    const { topGroups, bottomGroups, frictionGroups } = frictionLayoutState;
    const { addMoneyActions, swiggyMoneyInfoActions } = useSwiggyMoneyActions();
    const { canShowTimer, closeTime, onComplete, closingTimerSource, onTimerLoad } = useCartTimer();

    const { handleCustomActionOnBanner, paymentMethodDowntimeBannerData } = usePaymentBanner({
        banner,
        paymentGroups: _paymentGroups,
    });

    const {
        showPaymentMethodDowntimeHalfCard,
        closeHalfCard,
        onHalfCardCtaClick,
        downtimeMethodDisplayName,
        onHalfCardLoad,
    } = paymentMethodDowntimeBannerData;

    const handleDeeplinkNavigationOnBanner = useCallback(
        (id: string, link: string) => {
            paymentBannerClickEvent(id, clickEvent);
            const deeplinkUrl = format(SWIGGY_BANNER_CALLBACK_URL, link, window.location.href);
            const data: WebNavigateRequestPayload = {
                callbackUrl: window.location.href,
                openAsExternal: false,
                showBack: false,
                title: "Swiggy Money",
                url: deeplinkUrl,
                showLoader: false,
            };
            getBridgeSdk()
                .webNavigate(data)
                .catch(() => void 0);
        },
        [clickEvent],
    );

    const onBannerClick = useCallback(
        (
            link: string,
            type: string,
            id: string,
            actions: {
                paymentActions: Pick<PaymentGroupHandlerProps, "selectedPaymentId" | "onSelect" | "onPay">;
                onMorePaymentsOptionSelect: (
                    paymentGroup: PaymentGroupDataDef,
                    frictionGroups?: PaymentGroupDataDef[],
                ) => void;
            },
        ) => {
            // TODO: handle scroll type
            switch (type) {
                case BANNER_ACTION_TYPE.SCROLL:
                    return;

                case BANNER_ACTION_TYPE.CUSTOM:
                    handleCustomActionOnBanner(id, actions);
                    return;

                case BANNER_ACTION_TYPE.DEEPLINK:
                    handleDeeplinkNavigationOnBanner(id, link);
                    return;
            }
        },
        [handleCustomActionOnBanner, handleDeeplinkNavigationOnBanner],
    );

    const paymentGroupBanner = useMemo(() => {
        let bannerData: PaymentGroupBannerData | undefined;
        const swiggyInstrumentData = findSwiggyInstrumentFromSplitPayList(splitpayList);
        if (swiggyInstrumentData?.meta?.paymentGroupBannerData) {
            try {
                bannerData = safeJsonParse(
                    swiggyInstrumentData?.meta?.paymentGroupBannerData,
                ) as PaymentGroupBannerData;
            } catch {
                void 0;
            }
        } else {
            const swiggyMoneyPaymentMethodOption = findSwiggyInstrumentsPaymentMethod(
                _paymentGroups,
                SWIGGY_PAY_PAYMENT_CODE,
            );
            if (swiggyMoneyPaymentMethodOption.isSome()) {
                const swiggyMoneyPaymentMethod = swiggyMoneyPaymentMethodOption.unwrap();
                if (swiggyMoneyPaymentMethod?.meta?.paymentGroupBannerData)
                    try {
                        bannerData = safeJsonParse(
                            swiggyMoneyPaymentMethod?.meta?.paymentGroupBannerData,
                        ) as PaymentGroupBannerData;
                    } catch {
                        void 0;
                    }
            }
        }
        if (bannerData && bannerData?.asset_url) {
            const onViewPaymentGroupBanner = (): void => {
                unboxingBannerImpressionEvent(
                    bannerData?.analytics_data?.value ?? "",
                    bannerData?.analytics_data?.context ?? "",
                    impressionEvent,
                );
            };
            return (
                <PaymentBanner
                    banner={{
                        bannerType: BANNER_TYPE.IMAGEBANNER,
                        backgroundImage: getCloudinaryIdFromURL(bannerData?.asset_url),
                        id: bannerData?.analytics_data?.context,
                    }}
                    onViewBanner={onViewPaymentGroupBanner}
                    containerClassName={styles.paymentGroupBanner}
                />
            );
        }
        return null;
    }, [_paymentGroups, impressionEvent, splitpayList]);

    const onViewBanner = useCallback(
        (bannerData: BannerData) => {
            paymentBannerImpressionEvent(bannerData.id, impressionEvent);

            if (isInAppUPIBanner(bannerData)) {
                const bannerParams = getBannerParams(bannerData);
                // api call to merch server to update the banner frequency
                updateFrequencyCounterPublic(apiHost, bannerParams);
            }
        },
        [apiHost, impressionEvent],
    );

    const renderPaymentGroups = useCallback(
        (
            paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
            paymentActions: Pick<PaymentGroupHandlerProps, "selectedPaymentId" | "onSelect" | "onPay">,
        ): ReactElement[] | null => {
            if (paymentGroups == null || paymentGroups.length === 0) {
                return null;
            }
            return paymentGroups.map((paymentGroup) => (
                <PaymentGroupHandler
                    paymentAmount={String(amountToPay)}
                    paymentGroup={paymentGroup}
                    key={paymentGroup.groupName}
                    paymentInProgress={false}
                    paymentGroupBanner={paymentGroupBanner}
                    {...paymentActions}
                    {...props}
                />
            ));
        },
        [amountToPay, paymentGroupBanner, props],
    );

    const renderPaymentBanner = useCallback(
        (actions: {
            paymentActions: Pick<PaymentGroupHandlerProps, "selectedPaymentId" | "onSelect" | "onPay">;
            onMorePaymentsOptionSelect: (
                paymentGroup: PaymentGroupDataDef,
                frictionGroups?: PaymentGroupDataDef[],
            ) => void;
        }): ReactElement | null => {
            const canShowPaymentBannerParams = {
                paymentGroups,
                couponApplicablePaymentGroups,
                couponApplied,
                showCouponGroupsOnly,
                banner,
            };

            if (!canShowPaymentBanner(canShowPaymentBannerParams)) {
                return null;
            }

            return (
                <PaymentBanner
                    banner={banner as Required<GetPaymentMethodsResponse>["banner"]}
                    onClick={(link: string, type: string, id: string) => onBannerClick(link, type, id, actions)}
                    onViewBanner={onViewBanner}
                    bannerDimensions={getBannerDimensions(banner)}
                />
            );
        },
        [
            banner,
            paymentGroups,
            couponApplicablePaymentGroups,
            couponApplied,
            showCouponGroupsOnly,
            onBannerClick,
            onViewBanner,
        ],
    );

    const paylaterGroup = useMemo(() => getPayLaterPaymentGroup(_paymentGroups), [_paymentGroups]);
    const offerHeaderText = useMemo(() => paymentResponse?.offerHeaderText || "", [paymentResponse?.offerHeaderText]);

    const _showAllMethods = useCallback(() => {
        if (paymentResponse?.rewardApplied) {
            // unselect splitpay
            const selectedMethod = getSelectedSplitpayMethodData(splitpayList ?? []);
            !isEmpty(selectedMethod) && onSplitPayToggle(false, selectedMethod);
        } else {
            showAllMethods();
        }
    }, [paymentResponse?.rewardApplied, splitpayList, onSplitPayToggle, showAllMethods]);

    return (
        <>
            {paymentBannerMessage && <BannerMessage message={paymentBannerMessage} />}
            <PaymentActionProvider>
                {({
                    placeOrderInProgress,
                    inAppUpiHalfCardData,
                    onMorePaymentsOptionSelect,
                    ...paymentActions
                }): ReactElement => (
                    <>
                        <PaymentUIHandlerWrapper
                            showFilteredGroupsOnly={showFilteredGroupsOnly}
                            showAllMethods={_showAllMethods}
                            couponCode={couponCode}
                            paymentCouponCode={paymentCouponCode}
                            rewardName={rewardName}
                            canShowAllMethods={canShowAllMethods}
                            offerStrip={offerStrip}
                            goToOffersPage={goToOffersPage}
                            isIrctCart={!!checkoutMetaInfo?.irctc_data}
                            offerHeaderText={offerHeaderText}
                        >
                            {canShowTimer && (
                                <CartTimer
                                    duration={closeTime}
                                    source={closingTimerSource}
                                    onComplete={onComplete}
                                    onTimerLoad={onTimerLoad}
                                />
                            )}
                            {renderPaymentBanner({
                                paymentActions,
                                onMorePaymentsOptionSelect,
                            })}
                            {showSplitPay && splitpayList && (
                                <SplitPayBannerList
                                    selectedSplitpayMethods={selectedSplitPayMethods}
                                    splitPayList={splitpayList}
                                    handleChange={onSplitPayToggle}
                                    addMoneyActions={addMoneyActions}
                                    paymentGroupBanner={paymentGroupBanner}
                                    swiggyMoneyInfoActions={swiggyMoneyInfoActions}
                                    isAddMoneyCtaDisabled={isNonEmptyString(paymentActions.selectedPaymentId)}
                                    onFlipkartToolTipShow={onFlipkartToolTipShow}
                                    handleViewAllRewardsClick={setIsRewardsBottomSheetVisible}
                                    groupDisplayName={paymentResponse?.splitpayGroupDisplayName}
                                    couponApplied={couponApplied}
                                    rewardApplied={paymentResponse?.rewardApplied}
                                    analyticsEvents={splitpayAnalyticsEvents}
                                />
                            )}
                            {renderPaymentGroups(topGroups, paymentActions)}
                            <FrictionGroupRenderer
                                onOptionClick={onMorePaymentsOptionSelect}
                                frictionGroups={frictionGroups}
                            />
                            {renderPaymentGroups(bottomGroups, paymentActions)}
                        </PaymentUIHandlerWrapper>

                        {placeOrderInProgress || isLoading ? <PaymentLoader /> : null}

                        <GenericCrouton {...croutonState} />

                        {showPaymentMethodDowntimeHalfCard && (
                            <PaymentMethodDowntimeHalfCard
                                onCloseBs={closeHalfCard}
                                onCtaClick={onHalfCardCtaClick}
                                downtimeMethodDisplayName={downtimeMethodDisplayName}
                                onLoad={onHalfCardLoad}
                            />
                        )}

                        {!isEmpty(paymentResponse?.halfCardAwareness) && (
                            <HalfCard
                                isOpen={inAppUpiHalfCardData.showHalfCard}
                                onClose={inAppUpiHalfCardData.closeHalfCard}
                                hasCustomPadding
                                closeOnOverlayClick
                            >
                                <InAppUpiAwarenessHalfCard
                                    onLoad={inAppUpiHalfCardData.onHalfCardLoad}
                                    isOpen={inAppUpiHalfCardData.showHalfCard}
                                    onCtaClick={inAppUpiHalfCardData.onInAppUpiHalfCardCtaClick}
                                    halfCardData={paymentResponse?.halfCardAwareness}
                                />
                            </HalfCard>
                        )}

                        {isRewardsBottomSheetVisible && showSplitPay && !isEmpty(splitpayList) && (
                            <SplitPayRewardsHalfCard
                                splitPayList={splitpayList}
                                handleVisibility={setIsRewardsBottomSheetVisible}
                                selectedSplitpayMethods={selectedSplitPayMethods}
                                handleChange={onSplitPayToggle}
                                couponApplied={couponApplied}
                                rewardApplied={paymentResponse?.rewardApplied}
                                analyticsEvents={splitpayAnalyticsEvents}
                            />
                        )}

                        {checkoutState.type === CheckoutActionTypes.ADD_MONEY_CONFIRM && (
                            <AddMoneyConfirm payAction={paymentActions.onPay} paymentAmount={amountToPay} />
                        )}

                        {checkoutState.type === CheckoutActionTypes.POST_SWIGGY_ADD_MONEY && (
                            <AddSwiggyMoneyConfirm payAction={paymentActions.onPay} paymentAmount={amountToPay} />
                        )}

                        {/* in the case of PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER we are rendeing the
                        component AddPaylaterOrderConfirm and also passing the walletcode that we are getting in
                        the payload */}

                        {(checkoutState.type === CheckoutActionTypes.POST_PAYLATER_LINKING ||
                            checkoutState.type ===
                                CheckoutActionTypes.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER) && (
                            <AddPaylaterOrderConfirm
                                payAction={paymentActions.onPay}
                                payLaterPaymentCode={checkoutState?.payload?.paymentCode}
                            />
                        )}

                        {/* in the case of PAYLATER_SIMPL_LINKING_FAILED we are rendeing the
                        component PaylaterLinkingFailed and also passing the error that we are getting in
                        the payload */}

                        {checkoutState.type === CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED && (
                            <PaylaterLinkingFailed
                                error={checkoutState?.payload?.error}
                                paymentGroup={paylaterGroup.unwrap()}
                                paymentCode={checkoutState?.payload?.paymentCode}
                            />
                        )}

                        {checkoutState.type === CheckoutActionTypes.PAYMENT_FAILED && (
                            <PaymentFailed
                                payAction={paymentActions.onPay}
                                selectAction={paymentActions.onSelect}
                                paymentGroups={_paymentGroups}
                                amountToPay={String(amountToPay)}
                                itemQuantity={itemQuantity}
                                payload={checkoutState.payload}
                            />
                        )}

                        {checkoutState.type === CheckoutActionTypes.ORDER_INTERRUPTION && (
                            <OrderInterruptionAlert payAction={paymentActions.onPay} payload={checkoutState.payload} />
                        )}

                        {checkoutState.type === CheckoutActionTypes.WEBVIEW_REFRESH_ORDER_ID_MISMATCH_ALERT && (
                            <WebviewRefreshOrderIdMismatchAlert payload={checkoutState?.payload} />
                        )}

                        {checkoutState.type === CheckoutActionTypes.INVALID_COUPON_ALERT && (
                            <InvalidCouponAlert
                                payAction={paymentActions.onPay}
                                paymentGroups={_paymentGroups}
                                amountToPay={String(amountToPay)}
                                itemQuantity={itemQuantity}
                                payload={checkoutState.payload}
                                amountToPayWithoutCouponWithoutSplitpay={String(
                                    amountToPayWithoutCouponWithoutSplitpay,
                                )}
                            />
                        )}
                        {checkoutState.type === CheckoutActionTypes.GENERIC_PAYMENT_ALERT && (
                            <GenericPaymentAlert {...checkoutState.payload} />
                        )}

                        {checkoutState.type === CheckoutActionTypes.GENERIC_PAYMENT_TOAST && (
                            <GenericPaymentToast {...checkoutState.payload} />
                        )}

                        {checkoutState.type === CheckoutActionTypes.PAY_ON_DELIVERY_PAYMENT_ALREADY_DONE && (
                            <PaymentAlreadyDoneAlert payload={checkoutState.payload} />
                        )}
                    </>
                )}
            </PaymentActionProvider>
        </>
    );
});
